import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./utils/authContext";
import { getUserSettings, updateUserSettings } from "./utils/firestoreService";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./utils/firebase";
import { meetsMinRole } from "./utils/roleUtils";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CompanyAdminUsers from "./components/company-admin/CompanyAdminUsers";
import CompanyAdminStats from "./components/company-admin/CompanyAdminStats";
import CompanyAdminInfo from "./components/company-admin/CompanyAdminInfo";
import "./css/company-admin.css";

const CompanyAdmin = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const userId = currentUser?.uid;
  const [isLoading, setIsLoading] = useState(true);
  const [companyId, setCompanyId] = useState(null);
  const [adminPrefs, setAdminPrefs] = useState({});
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const init = async () => {
      if (!userId) return;
      try {
        const userDoc = await getDoc(doc(db, "users", userId));
        if (!userDoc.exists()) {
          navigate("/dashboard");
          return;
        }
        const userData = userDoc.data();

        // Role gate: companyAdmin or superAdmin
        if (!meetsMinRole(userData.userRole, "companyAdmin")) {
          console.warn("Access denied: User is not companyAdmin+");
          navigate("/dashboard");
          return;
        }

        if (!userData.companyId) {
          setCompanyId(null);
          setIsLoading(false);
          return;
        }

        setCompanyId(userData.companyId);

        // Load saved tab preference
        const settings = await getUserSettings(userId);
        const prefs = settings.companyAdminPreferences || {};
        setAdminPrefs(prefs);
        if (prefs.lastTab !== undefined) {
          setActiveTab(prefs.lastTab);
        }
      } catch (error) {
        console.error("Error initializing company admin:", error);
        navigate("/dashboard");
      }
      setIsLoading(false);
    };

    init();
  }, [userId, navigate]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (userId) {
      const updatedPrefs = { ...adminPrefs, lastTab: newValue };
      setAdminPrefs(updatedPrefs);
      updateUserSettings(userId, { companyAdminPreferences: updatedPrefs });
    }
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="header-container">
          <div className="page-title">My Team</div>
        </div>
      </div>
    );
  }

  if (!companyId) {
    return (
      <div className="page-container">
        <div className="header-container">
          <div className="page-title">My Team</div>
        </div>
        <div style={{ color: "var(--color-text-secondary)", marginTop: "24px" }}>
          No company associated with your account. Contact a superAdmin to assign you to a company.
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="company-admin-header header-container" style={{ justifyContent: "space-between" }}>
        <div className="page-title" style={{ marginBottom: 0 }}>My Team</div>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="company admin tabs"
          textColor="primary"
          indicatorColor="primary"
          sx={{ minHeight: "auto" }}
        >
          <Tab label="Users" sx={{ minHeight: "auto", py: 1 }} />
          <Tab label="Stats" sx={{ minHeight: "auto", py: 1 }} />
          <Tab label="Company Info" sx={{ minHeight: "auto", py: 1 }} />
        </Tabs>
      </div>

      {activeTab === 0 && (
        <CompanyAdminUsers
          companyId={companyId}
          userId={userId}
          adminPrefs={adminPrefs}
          setAdminPrefs={setAdminPrefs}
        />
      )}
      {activeTab === 1 && (
        <CompanyAdminStats
          companyId={companyId}
          userId={userId}
          adminPrefs={adminPrefs}
          setAdminPrefs={setAdminPrefs}
        />
      )}
      {activeTab === 2 && (
        <CompanyAdminInfo companyId={companyId} />
      )}
    </div>
  );
};

export default CompanyAdmin;
